/* The new connection journey: the trail, the wizard's stepper, its step card with the moves bar on
   top, the transmitter cards, option cards and fields, the review, the summary beside the step and
   the transmitter's return; on top of the shared receptora styles, colours from the Finnest tokens. */

.journey-page {
  display: grid;
  gap: 18px;
}

.journey-trail {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  color: var(--color-finnest-muted);
  font-size: 12.5px;
}

.journey-trail a {
  color: var(--color-finnest-muted);
  font-weight: 600;
  text-decoration: none;
}

.journey-trail a:hover {
  color: var(--color-finnest-accent);
}

.journey-trail svg {
  width: 12px;
  height: 12px;
}

.journey-trail [aria-current='page'] {
  color: var(--color-finnest-ink);
  font-weight: 650;
}

.journey-note {
  margin: 0;
  color: var(--color-finnest-muted);
  font-size: 12px;
}

.journey-note[data-state='bad'] {
  color: #b42318;
}

.journey-link {
  border: 0;
  background: none;
  padding: 0;
  color: var(--color-finnest-accent);
  font-size: 12.5px;
  font-weight: 650;
  cursor: pointer;
}

.journey-link:disabled {
  color: var(--color-finnest-muted);
  cursor: not-allowed;
}

.journey-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--color-finnest-border);
  border-radius: 999px;
  background: var(--color-finnest-surface);
  padding: 3px 10px;
  color: var(--color-finnest-ink);
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
}

.journey-chip b {
  color: var(--color-finnest-muted);
  font-variant-numeric: tabular-nums;
  font-weight: 750;
}

.journey-chip[aria-pressed='true'] {
  border-color: var(--color-finnest-accent);
  background: var(--color-finnest-accent-soft);
  color: var(--color-finnest-accent-strong);
}

.journey-chip[aria-pressed='true'] b {
  color: var(--color-finnest-accent-strong);
}

.journey-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.journey-notice {
  display: grid;
  gap: 12px;
  border: 1px solid var(--color-finnest-border);
  border-radius: 8px;
  background: var(--color-finnest-surface);
  padding: 20px;
}

.journey-notice h2,
.journey-notice p {
  margin: 0;
}

/* The stepper: four steps on one line, joined by a line that fills as they are done. */
.journey-stepper {
  display: flex;
  overflow-x: auto;
  margin: 0;
  border: 1px solid var(--color-finnest-border);
  border-radius: 10px;
  background: var(--color-finnest-surface);
  padding: 14px 16px;
  list-style: none;
}

.journey-stepper li {
  display: flex;
  flex: 1 1 0;
  align-items: center;
  min-width: 150px;
}

.journey-stepper li:not(:last-child)::after {
  content: '';
  flex: 1;
  min-width: 16px;
  height: 2px;
  margin: 0 10px;
  border-radius: 2px;
  background: var(--color-finnest-border);
}

.journey-stepper li[data-state='done']:not(:last-child)::after {
  background: var(--color-finnest-accent);
}

.journey-stepper button {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 0;
  border-radius: var(--radius-finnest-control);
  background: none;
  padding: 0;
  color: inherit;
  text-align: left;
}

.journey-stepper button:disabled {
  cursor: default;
}

.journey-stepper li[data-state='done'] button:not(:disabled) {
  cursor: pointer;
}

.journey-stepper button:focus-visible {
  outline: 0;
  box-shadow: var(--shadow-finnest-focus);
}

.journey-stepper-dot {
  display: grid;
  flex: none;
  place-items: center;
  width: 28px;
  height: 28px;
  border: 2px solid var(--color-finnest-border);
  border-radius: 999px;
  background: var(--color-finnest-surface);
  color: var(--color-finnest-muted);
  font-size: 12.5px;
  font-weight: 800;
}

.journey-stepper-dot svg {
  width: 14px;
  height: 14px;
}

.journey-stepper li[data-state='current'] .journey-stepper-dot {
  border-color: var(--color-finnest-accent);
  box-shadow: var(--shadow-finnest-focus);
  color: var(--color-finnest-accent-strong);
}

.journey-stepper li[data-state='done'] .journey-stepper-dot {
  border-color: var(--color-finnest-accent);
  background: var(--color-finnest-accent);
  color: #ffffff;
}

.journey-stepper strong {
  display: block;
  color: var(--color-finnest-ink);
  font-size: 13px;
  white-space: nowrap;
}

.journey-stepper li[data-state='next'] strong {
  color: var(--color-finnest-muted);
}

.journey-stepper small {
  display: block;
  max-width: 150px;
  overflow: hidden;
  color: var(--color-finnest-muted);
  font-size: 11.5px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The step and the summary side by side on a wide screen, the summary under it otherwise. */
.journey-wizard {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 16px;
  align-items: start;
}

.journey-step,
.journey-summary,
.journey-return {
  min-width: 0;
  border: 1px solid var(--color-finnest-border);
  border-radius: 8px;
  background: var(--color-finnest-surface);
}

/* The moves bar: on top of the step card, in view while the step scrolls. */
.journey-moves {
  position: sticky;
  top: 0;
  z-index: 3;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid var(--color-finnest-border);
  border-radius: 8px 8px 0 0;
  background: #fcfbff;
  padding: 14px 20px;
}

.journey-moves-hint {
  flex: 1;
  color: var(--color-finnest-muted);
  font-size: 12.5px;
}

.journey-moves-hint b {
  color: var(--color-finnest-ink);
}

.journey-moves .fui-button svg {
  width: 15px;
  height: 15px;
}

.journey-step-body {
  display: grid;
  gap: 18px;
  padding: 20px;
}

.journey-step-body > header h2 {
  margin: 0;
  color: var(--color-finnest-ink);
}

.journey-step-body > header h2:focus {
  outline: 0;
}

.journey-step-body > header p {
  margin: 4px 0 0;
  color: var(--color-finnest-muted);
}

.journey-group {
  display: grid;
  gap: 10px;
}

.journey-section-title {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}

.journey-section-title h3,
.journey-subtitle {
  margin: 0;
  font-size: 14px;
}

/* Transmitters: the search bar, then one card each. */
.journey-tx-tools {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.journey-search {
  flex: 1 1 260px;
  max-width: 420px;
}

.receptora-page .journey-search input {
  width: 100%;
  max-width: none;
}

.journey-tx-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.journey-tx {
  position: relative;
  display: grid;
  gap: 8px;
  align-content: start;
  border: 1px solid var(--color-finnest-border);
  border-radius: 8px;
  background: var(--color-finnest-surface);
  padding: 12px 14px;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
}

.journey-tx[data-open]:hover {
  border-color: rgb(91 50 230 / 35%);
  box-shadow: var(--shadow-finnest-subtle);
}

.journey-tx[data-chosen] {
  border-color: var(--color-finnest-accent);
  background: #fcfbff;
  box-shadow: var(--shadow-finnest-focus);
}

.journey-tx:not([data-open]) {
  background: #fafafb;
}

.journey-tx:not([data-open]) .journey-tx-who {
  opacity: 0.6;
}

.journey-tx-check {
  position: absolute;
  top: 10px;
  right: 10px;
  display: grid;
  place-items: center;
  width: 20px;
  height: 20px;
  border-radius: 999px;
  background: var(--color-finnest-accent);
  color: #ffffff;
}

.journey-tx-check svg {
  width: 12px;
  height: 12px;
}

.journey-tx-who {
  display: flex;
  align-items: center;
  gap: 10px;
}

.journey-tx-who strong,
.journey-tx-who button {
  display: block;
  border: 0;
  background: none;
  padding: 0;
  color: var(--color-finnest-ink);
  font: inherit;
  font-weight: 650;
  text-align: left;
}

/* The card's button covers the whole card, so any of it chooses; the details stay above it. */
.journey-tx-who button {
  cursor: pointer;
}

.journey-tx-who button::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 8px;
}

.journey-tx-who button:focus-visible {
  outline: 0;
}

.journey-tx-who button:focus-visible::after {
  box-shadow: var(--shadow-finnest-focus);
}

.journey-tx-who button[aria-disabled='true'] {
  cursor: not-allowed;
}

.journey-tx-org {
  display: block;
  color: var(--color-finnest-muted);
  font-size: 12px;
}

.journey-tx-details {
  display: grid;
  gap: 8px;
  justify-items: start;
}

.journey-tx-why {
  display: block;
  margin: 0;
  color: var(--color-finnest-muted);
  font-size: 12.5px;
}

.journey-tx details {
  position: relative;
  z-index: 1;
  color: var(--color-finnest-muted);
  font-size: 12px;
}

.journey-tx summary {
  color: var(--color-finnest-accent);
  font-weight: 600;
  cursor: pointer;
}

.journey-tx details p {
  margin: 4px 0 0;
}

/* Option cards: a native checkbox or radio under a card that shows its state. */
.journey-page fieldset.journey-options,
.journey-options {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 8px;
  margin: 0;
  border: 0;
  background: none;
  padding: 0;
}

.journey-options[data-columns='wide'] {
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
}

.journey-option {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid var(--color-finnest-border);
  border-radius: 8px;
  background: var(--color-finnest-surface);
  padding: 10px 12px;
  color: var(--color-finnest-ink);
  font-weight: 600;
  cursor: pointer;
}

.journey-option input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.journey-option:has(input:checked) {
  border-color: var(--color-finnest-accent);
  background: var(--color-finnest-accent-soft);
  color: var(--color-finnest-accent-strong);
}

.journey-option:has(input:focus-visible) {
  box-shadow: var(--shadow-finnest-focus);
}

.journey-option-box {
  display: grid;
  flex: none;
  place-items: center;
  width: 18px;
  height: 18px;
  border: 2px solid #c9c9d1;
  border-radius: 5px;
}

.journey-option:has(input[type='radio']) .journey-option-box {
  border-radius: 999px;
}

.journey-option:has(input:checked) .journey-option-box {
  border-color: var(--color-finnest-accent);
  background: var(--color-finnest-accent);
  color: #ffffff;
}

.journey-option-box svg {
  width: 11px;
  height: 11px;
}

.journey-option small {
  display: block;
  color: var(--color-finnest-muted);
  font-size: 11.5px;
  font-weight: 500;
}

/* Fields: label, control, then help; a complete document shows whether it holds. */
.journey-field {
  display: grid;
  gap: 6px;
  max-width: 440px;
}

.journey-field[data-wide] {
  max-width: 640px;
}

.journey-field > label,
.journey-field > span:first-child {
  color: var(--color-finnest-ink);
  font-size: 13px;
  font-weight: 650;
}

.receptora-page .journey-field :is(input, textarea) {
  width: 100%;
  max-width: none;
  min-height: 38px;
}

.receptora-page .journey-field textarea {
  min-height: 96px;
  resize: vertical;
}

.journey-field[data-state='ok'] input {
  border-color: rgb(51 204 204 / 70%);
}

.journey-field[data-state='bad'] :is(input, textarea) {
  border-color: rgb(239 67 67 / 60%);
}

.journey-field-help {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  color: var(--color-finnest-muted);
  font-size: 12px;
}

.journey-field-help [data-state='bad'] {
  color: #b42318;
}

.journey-validity {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 650;
}

.journey-validity svg {
  width: 13px;
  height: 13px;
}

.journey-validity[data-state='ok'] {
  color: #0f6f74;
}

.journey-validity[data-state='bad'] {
  color: #b42318;
}

.journey-privacy {
  margin: 0;
  border: 1px solid rgb(51 204 204 / 28%);
  border-radius: 8px;
  background: var(--color-finnest-trust-soft);
  padding: 10px 12px;
  color: #0f5257;
  font-size: 12.5px;
}

.journey-segmented {
  display: inline-flex;
  justify-self: start;
  gap: 3px;
  border: 1px solid var(--color-finnest-border);
  border-radius: 8px;
  background: var(--color-finnest-muted-surface);
  padding: 3px;
}

.journey-segmented label {
  position: relative;
  border-radius: 6px;
  padding: 6px 14px;
  color: var(--color-finnest-muted);
  font-weight: 650;
  cursor: pointer;
}

.journey-segmented input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.journey-segmented label:has(input:checked) {
  background: var(--color-finnest-surface);
  box-shadow: 0 1px 3px rgb(22 11 51 / 12%);
  color: var(--color-finnest-ink);
}

.journey-segmented label:has(input:focus-visible) {
  box-shadow: var(--shadow-finnest-focus);
}

.journey-rules {
  display: grid;
  gap: 4px;
  margin: 0;
  padding: 0;
  font-size: 12.5px;
  list-style: none;
}

.journey-rules li {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--color-finnest-muted);
}

.journey-rules svg {
  width: 14px;
  height: 14px;
}

.journey-rule-mark {
  width: 10px;
  height: 10px;
  margin: 0 2px;
  border: 2px solid currentColor;
  border-radius: 999px;
}

.journey-rules li[data-state='ok'] {
  color: #0f6f74;
}

.journey-rules li[data-state='bad'] {
  color: #b42318;
}

.journey-months {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.journey-months .journey-chip {
  justify-content: center;
  min-width: 44px;
}

/* The review: one section per step, each with its way back. */
.journey-review {
  display: grid;
  gap: 10px;
}

.journey-review section {
  display: grid;
  gap: 8px;
  border: 1px solid var(--color-finnest-border);
  border-radius: 8px;
  padding: 12px 14px;
}

.journey-review section > header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.journey-review h3 {
  margin: 0;
  color: var(--color-finnest-muted);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.journey-terms {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 18px;
  margin: 0;
}

.journey-terms > div {
  min-width: 0;
  border-bottom: 1px dashed var(--color-finnest-border);
  padding: 7px 0;
}

.journey-terms > div[data-wide] {
  grid-column: 1 / -1;
}

.journey-terms dt {
  color: var(--color-finnest-muted);
  font-size: 11.5px;
}

.journey-terms dd {
  margin: 1px 0 0;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.journey-next-steps {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.journey-next-steps li {
  display: grid;
  gap: 4px;
  border-radius: 8px;
  background: var(--color-finnest-accent-soft);
  padding: 12px;
  color: var(--color-finnest-accent-strong);
  font-size: 12.5px;
}

.journey-next-steps b {
  color: var(--color-finnest-ink);
  font-size: 13px;
}

/* The summary beside the step, in view while it scrolls. */
.journey-summary {
  position: sticky;
  top: 16px;
}

.journey-summary h2 {
  margin: 0;
  border-bottom: 1px solid var(--color-finnest-border);
  padding: 14px 16px;
  font-size: 15px;
}

.journey-summary dl {
  display: grid;
  margin: 0;
  padding: 6px 16px 14px;
}

.journey-summary dl > div {
  display: grid;
  gap: 2px;
  border-bottom: 1px dashed var(--color-finnest-border);
  padding: 9px 0;
}

.journey-summary dl > div:last-child {
  border-bottom: 0;
}

.journey-summary dt {
  color: var(--color-finnest-muted);
  font-size: 12px;
}

.journey-summary dd {
  margin: 0;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.journey-summary dd small {
  display: block;
  color: var(--color-finnest-muted);
  font-weight: 500;
}

.journey-summary dd[data-pending] {
  color: #a1a1aa;
  font-style: italic;
  font-weight: 500;
}

/* The transmitter's return: the progress of the new connection while Power confirms it. */
.journey-return {
  display: grid;
  gap: 18px;
  width: 100%;
  max-width: 620px;
  margin: 24px auto;
  padding: 28px;
}

.journey-return h1 {
  margin: 0;
  font-size: 22px;
}

.journey-lead {
  display: block;
  color: var(--color-finnest-muted);
}

.journey-progress {
  display: grid;
  gap: 14px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.journey-progress li {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.journey-progress strong {
  display: block;
}

.journey-progress li span:not(.journey-progress-dot) {
  color: var(--color-finnest-muted);
  font-size: 12.5px;
}

.journey-progress-dot {
  display: grid;
  flex: none;
  place-items: center;
  width: 26px;
  height: 26px;
  border: 2px solid var(--color-finnest-border);
  border-radius: 999px;
  background: var(--color-finnest-surface);
  color: var(--color-finnest-muted);
}

.journey-progress-dot svg {
  width: 13px;
  height: 13px;
}

.journey-progress li[data-state='done'] .journey-progress-dot {
  border-color: var(--color-finnest-trust);
  background: var(--color-finnest-trust);
  color: #ffffff;
}

.journey-progress li[data-state='now'] .journey-progress-dot {
  border-color: var(--color-finnest-accent);
  border-top-color: transparent;
  animation: journey-spin 0.8s linear infinite;
}

@keyframes journey-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .journey-progress li[data-state='now'] .journey-progress-dot {
    border-top-color: var(--color-finnest-accent);
    animation: none;
  }
}

@media (max-width: 1180px) {
  .journey-wizard {
    grid-template-columns: minmax(0, 1fr);
  }

  .journey-summary {
    position: static;
  }
}

@media (max-width: 860px) {
  .journey-next-steps,
  .journey-terms {
    grid-template-columns: minmax(0, 1fr);
  }

  .journey-return {
    margin: 0;
    padding: 20px;
  }
}

/* A group of controls under one caption, as a field reads it. */
.journey-page fieldset.journey-field {
  display: grid;
  margin: 0;
  border: 0;
  background: none;
  padding: 0;
}

.journey-field > legend {
  margin-bottom: 6px;
  padding: 0;
  color: var(--color-finnest-ink);
  font-size: 13px;
  font-weight: 650;
}
